import type { CatalogueProductSummary } from '@enp/domain/api';
import { formatCount } from '@enp/domain/format';
import {
  Badge,
  cn,
  Button,
  createColumnHelper,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  toast,
  unitText,
  type ColumnDef,
  type SortingState,
} from '@enp/ui';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  Archive,
  BellDot,
  BookmarkPlus,
  ChevronDown,
  FileInput,
  GitCompareArrows,
  Layers,
  Search,
  SearchX,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { meQueryOptions } from '../../../auth/session.js';
import { ROLE_LABEL, ROLE_ORDER, SORT_LABEL } from '../../../catalogue/labels.js';
import {
  type CatalogueSearch,
  catalogueSearchSchema,
  categoriesQuery,
  cleanSearch,
  isFiltered,
  productListQuery,
} from '../../../catalogue/queries.js';
import {
  BUILT_IN_VIEWS,
  type CatalogueView,
  sameSearch,
  useSavedViews,
} from '../../../catalogue/saved-views.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { usePageSearch } from '../../../shell/signals.js';
import { useCan } from '../../../production/parts.js';
import { reviewItemsQuery } from '../../../production/queries.js';

export const Route = createFileRoute('/_app/catalogue/')({
  validateSearch: catalogueSearchSchema,
  component: CatalogueList,
});

const TABLE_LABEL = 'Products';
const ALL = 'all';

/** "Import changes to review (3)": shown only while an import waits for a decision (D-032). */
function ReviewLink() {
  // Review items carry supplier details and import costs: reports.view (CR5-9).
  const canRead = useCan('reports.view');
  const waiting = useInfiniteQuery({
    ...reviewItemsQuery('open'),
    enabled: canRead,
    select: (d) => d.pages[0]?.total ?? 0,
  });
  if (!waiting.data) return null;
  return (
    <Button variant="secondary" asChild>
      <Link to="/catalogue/review">
        <GitCompareArrows aria-hidden />
        Import changes to review ({formatCount(waiting.data)})
      </Link>
    </Button>
  );
}

// ---------------------------------------------------------------- cells

function RoleBadges({ roles }: { roles: CatalogueProductSummary['roles'] }) {
  const ordered = ROLE_ORDER.filter((r) => roles.includes(r));
  return (
    <span className="flex flex-wrap gap-1">
      {ordered.map((role) => (
        <Badge key={role} tone={role === 'sellable' ? 'primary' : 'neutral'}>
          {ROLE_LABEL[role]}
        </Badge>
      ))}
    </span>
  );
}

function ExpiryCell({ product }: { product: CatalogueProductSummary }) {
  if (product.state_rule) {
    return (
      <span className="flex flex-col whitespace-nowrap leading-tight">
        <span className="text-fg">{product.state_rule.name}</span>
        {product.state_rule.source === 'category' && (
          <span className="text-xs text-muted">From its category</span>
        )}
      </span>
    );
  }
  if (product.tracks_expiry)
    return <span className="whitespace-nowrap text-fg">Best-before only</span>;
  return <span className="whitespace-nowrap text-muted">Not tracked</span>;
}

function RecipeCell({ state }: { state: CatalogueProductSummary['recipe_state'] }) {
  if (state === 'active') return <StatusBadge status="success">In use</StatusBadge>;
  if (state === 'draft') return <StatusBadge status="warning">Draft only</StatusBadge>;
  return (
    <span className="text-muted">
      <span aria-hidden>—</span>
      <span className="sr-only">No recipe</span>
    </span>
  );
}

const col = createColumnHelper<CatalogueProductSummary>();

// TanStack's documented DataTable typing: columns carry heterogeneous value types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: ColumnDef<CatalogueProductSummary, any>[] = [
  col.accessor('name', {
    header: 'Name',
    meta: { width: 'min-w-56' },
    cell: ({ row }) => (
      <span className="flex items-center gap-2">
        <Link
          to="/catalogue/$productId"
          params={{ productId: row.original.id }}
          // The row is the keyboard target (Enter opens); the link serves the mouse.
          tabIndex={-1}
          className="font-medium text-fg hover:underline"
        >
          {row.original.name}
        </Link>
        {row.original.archived_at && (
          <Badge tone="outline">
            <Archive aria-hidden />
            Archived
          </Badge>
        )}
      </span>
    ),
  }),
  col.display({
    id: 'sku',
    header: 'SKU',
    enableSorting: false,
    cell: ({ row }) => {
      const [first, ...rest] = row.original.skus;
      if (!first) return <span className="text-muted">None</span>;
      return (
        <span className="whitespace-nowrap font-mono text-sm">
          {first}
          {rest.length > 0 && <span className="ml-1 font-body text-muted">+{rest.length}</span>}
        </span>
      );
    },
  }),
  col.display({
    id: 'roles',
    header: 'Roles',
    enableSorting: false,
    cell: ({ row }) => <RoleBadges roles={row.original.roles} />,
  }),
  col.display({
    id: 'category',
    header: 'Category',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.category ? (
        <span className="whitespace-nowrap text-fg">{row.original.category.name}</span>
      ) : (
        <span className="text-muted">None</span>
      ),
  }),
  col.accessor('base_uom', {
    header: 'Unit',
    enableSorting: false,
    // Secondary below 1440 px (1280, tablet): the detail page has it (UI1A-6).
    meta: { className: 'hidden min-[90rem]:table-cell' },
    cell: ({ getValue }) => (
      <span className="whitespace-nowrap">{unitText(getValue<string>(), '2') || 'each'}</span>
    ),
  }),
  col.display({
    id: 'expiry',
    header: 'Expiry states',
    enableSorting: false,
    cell: ({ row }) => <ExpiryCell product={row.original} />,
  }),
  col.display({
    id: 'recipe',
    header: 'Recipe',
    enableSorting: false,
    cell: ({ row }) => <RecipeCell state={row.original.recipe_state} />,
  }),
  col.accessor('identifier_count', {
    header: 'Codes',
    enableSorting: false,
    meta: { align: 'right', className: 'hidden min-[90rem]:table-cell' },
    cell: ({ getValue }) => <span className="tabular-nums">{getValue<number>()}</span>,
  }),
];

// ---------------------------------------------------------------- toolbar

interface FilterSelectProps {
  label: string;
  value: string | undefined;
  allLabel: string;
  options: { value: string; label: string }[];
  onChange: (value: string | undefined) => void;
  className?: string;
}

/** A filter as a select that sizes to its value; an active filter is tinted. */
function FilterSelect({ label, value, allLabel, options, onChange, className }: FilterSelectProps) {
  return (
    <Select value={value ?? ALL} onValueChange={(v) => onChange(v === ALL ? undefined : v)}>
      <SelectTrigger
        aria-label={label}
        data-active={value !== undefined || undefined}
        className={cn(
          'w-auto min-w-28 max-w-52',
          'data-active:border-primary/60 data-active:bg-primary-subtle data-active:text-primary-text',
          className,
        )}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{allLabel}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function SaveViewDialog({
  open,
  onOpenChange,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (name: string) => void;
}) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | undefined>();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Give the view a name, like “Cakes to check”.');
      return;
    }
    onSave(name);
    setName('');
    setError(undefined);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Save this view</DialogTitle>
          <DialogDescription>
            Keeps the current search and filters under a name, on this browser, for you.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-5" noValidate>
          <FormField label="Name" error={error} required>
            <Input
              value={name}
              maxLength={60}
              autoFocus
              onChange={(e) => setName(e.target.value)}
            />
          </FormField>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">Save view</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ViewsMenu({
  userId,
  search,
  onApply,
}: {
  userId: string;
  search: CatalogueSearch;
  onApply: (view: CatalogueView) => void;
}) {
  const { views, save, remove } = useSavedViews(userId);
  const [saving, setSaving] = useState(false);
  const all = [...BUILT_IN_VIEWS, ...views];
  const current = all.find((v) => sameSearch(v.search, search));
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="secondary" icon={<Layers aria-hidden />}>
            <span className="max-w-40 truncate">
              <span className="sr-only">View: </span>
              {current?.name ?? 'Custom view'}
            </span>
            <ChevronDown aria-hidden className="text-muted" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>Views</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={current?.id ?? ''}
            onValueChange={(id) => {
              const view = all.find((v) => v.id === id);
              if (view) onApply(view);
            }}
          >
            {BUILT_IN_VIEWS.map((v) => (
              <DropdownMenuRadioItem key={v.id} value={v.id}>
                {v.name}
              </DropdownMenuRadioItem>
            ))}
            {views.length > 0 && <DropdownMenuSeparator />}
            {views.map((v) => (
              <DropdownMenuRadioItem key={v.id} value={v.id}>
                <span className="truncate">{v.name}</span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setSaving(true)} disabled={Boolean(current)}>
            <BookmarkPlus aria-hidden />
            Save this view…
          </DropdownMenuItem>
          {views.length > 0 && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Trash2 aria-hidden />
                Delete a saved view
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-56">
                {views.map((v) => (
                  <DropdownMenuItem
                    key={v.id}
                    destructive
                    onSelect={() => {
                      remove(v.id);
                      toast.success(`Deleted the view “${v.name}”`);
                    }}
                  >
                    <span className="truncate">{v.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <SaveViewDialog
        open={saving}
        onOpenChange={setSaving}
        onSave={(name) => {
          const view = save(name, search);
          setSaving(false);
          toast.success(`Saved the view “${view.name}”`, {
            description: 'Find it under Views. It’s kept on this browser.',
          });
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------- page

function CatalogueList() {
  useDocumentTitle('Catalogue');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const searchRef = useRef<HTMLInputElement>(null);
  usePageSearch(searchRef);

  const setSearch = (patch: Partial<CatalogueSearch>, replace = false) =>
    void navigate({ search: (prev) => cleanSearch({ ...prev, ...patch }), replace });

  // The search box types freely; the URL (and the server search) follows after a pause.
  const [text, setText] = useState(search.q ?? '');
  const pushed = useRef(search.q ?? '');
  useEffect(() => {
    // Changed from outside (a view, the top-bar search, Back): show it.
    if ((search.q ?? '') !== pushed.current) {
      pushed.current = search.q ?? '';
      setText(search.q ?? '');
    }
  }, [search.q]);
  useEffect(() => {
    const q = text.trim();
    if (q === pushed.current) return;
    const timer = setTimeout(() => {
      pushed.current = q;
      void navigate({
        search: (prev) => cleanSearch({ ...prev, q: q || undefined }),
        replace: true,
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [text, navigate]);

  const categories = useQuery(categoriesQuery());
  const list = useInfiniteQuery({ ...productListQuery(search), placeholderData: keepPreviousData });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const total = list.data?.pages[0]?.total ?? 0;
  const filtered = isFiltered(search);

  const sorting: SortingState =
    search.sort === '-name'
      ? [{ id: 'name', desc: true }]
      : search.sort === undefined || search.sort === 'name'
        ? [{ id: 'name', desc: false }]
        : [];
  const onSortingChange = (next: SortingState) => {
    const name = next.find((s) => s.id === 'name');
    setSearch({ sort: name?.desc ? '-name' : undefined });
  };

  const clearAll = () => {
    pushed.current = '';
    setText('');
    void navigate({ search: search.sort ? { sort: search.sort } : {} });
  };

  const focusFirstRow = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'ArrowDown') return;
    const row = document.querySelector<HTMLTableRowElement>(
      `table[aria-label="${TABLE_LABEL}"] tbody tr[tabindex="0"]`,
    );
    if (row) {
      e.preventDefault();
      row.focus();
    }
  };

  const openProduct = (p: CatalogueProductSummary) =>
    void navigate({ to: '/catalogue/$productId', params: { productId: p.id } });

  const firstLoad = list.isPending;
  const failed = list.isError && !list.data;

  const empty = filtered ? (
    <EmptyState
      compact
      icon={SearchX}
      title="No products match"
      description={
        search.q
          ? `Nothing in the catalogue matches “${search.q}” with these filters. Check the spelling, or search by SKU or barcode.`
          : search.attention
            ? 'Nothing needs attention: every recipe is signed off and every sales-channel link is checked.'
            : 'No products fit these filters.'
      }
      action={
        <Button variant="secondary" icon={<X aria-hidden />} onClick={clearAll}>
          Clear search and filters
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={FileInput}
      title="Your catalogue is empty"
      description="Bring in your products, recipes and codes from your till system in one step. You’ll see what will change before anything is saved."
      action={
        <Button asChild>
          <Link to="/imports">Import your catalogue</Link>
        </Button>
      }
    />
  );

  const loaded = items.length;
  const footer =
    !firstLoad && !failed && total > 0 ? (
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-muted" aria-live="polite">
          {loaded >= total
            ? `${formatCount(total)} ${total === 1 ? 'product' : 'products'}`
            : `Showing ${formatCount(loaded)} of ${formatCount(total)} products`}
          {list.isFetching && !list.isFetchingNextPage && ' · Updating…'}
        </p>
        {list.hasNextPage && (
          <Button
            variant="secondary"
            size="sm"
            loading={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
          >
            {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        )}
        {list.isError && list.data && (
          <p role="alert" className="text-sm text-state-danger">
            The next products didn’t load.{' '}
            <button
              type="button"
              className="font-medium underline underline-offset-2"
              onClick={() => void list.fetchNextPage()}
            >
              Try again
            </button>
          </p>
        )}
      </div>
    ) : undefined;

  return (
    <>
      <PageHeader
        title="Catalogue"
        description="Everything you make, buy and sell: products, recipes, expiry states and codes."
        // Products are edited on their own page; here, the import changes waiting for a
        // decision and the saved views.
        actions={
          <>
            <ReviewLink />
            <ViewsMenu
              userId={me.user.id}
              search={search}
              onApply={(view) => void navigate({ search: view.search })}
            />
          </>
        }
      />

      <div
        className="mb-3 flex flex-wrap items-center gap-2"
        role="search"
        aria-label="Catalogue search"
      >
        <div className="relative w-full min-w-44 sm:w-auto sm:max-w-72 sm:flex-1">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted"
          />
          <Input
            ref={searchRef}
            type="search"
            aria-label="Search products"
            aria-keyshortcuts="/"
            placeholder="Name, SKU or barcode"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={focusFirstRow}
            className="pl-8"
          />
        </div>
        <FilterSelect
          label="Role"
          value={search.role}
          allLabel="All roles"
          options={ROLE_ORDER.map((r) => ({ value: r, label: ROLE_LABEL[r] }))}
          onChange={(v) => setSearch({ role: v as CatalogueSearch['role'] })}
        />
        <FilterSelect
          label="Category"
          value={search.category}
          allLabel="All categories"
          options={(categories.data?.items ?? []).map((c) => ({ value: c.id, label: c.name }))}
          onChange={(v) => setSearch({ category: v })}
        />
        <FilterSelect
          label="Expiry states"
          value={search.expiry}
          allLabel="Any expiry states"
          options={[
            { value: 'tracked', label: 'With expiry states' },
            { value: 'untracked', label: 'Without expiry states' },
          ]}
          onChange={(v) => setSearch({ expiry: v as CatalogueSearch['expiry'] })}
        />
        <FilterSelect
          label="Archived"
          value={search.archived}
          allLabel="Live products"
          options={[
            { value: 'only', label: 'Archived only' },
            { value: 'include', label: 'Live and archived' },
          ]}
          onChange={(v) => setSearch({ archived: v as CatalogueSearch['archived'] })}
        />
        {search.attention && (
          <Button
            variant="secondary"
            size="sm"
            icon={<BellDot aria-hidden />}
            aria-label="Needs attention: show every product"
            onClick={() => setSearch({ attention: undefined })}
          >
            Needs attention
            <X aria-hidden className="text-muted" />
          </Button>
        )}
        {filtered && (
          <Button variant="ghost" icon={<X aria-hidden />} onClick={clearAll}>
            Clear
          </Button>
        )}
        <Select
          value={search.sort ?? 'name'}
          onValueChange={(v) =>
            setSearch({ sort: v === 'name' ? undefined : (v as CatalogueSearch['sort']) })
          }
        >
          <SelectTrigger aria-label="Sort" className="ml-auto w-auto min-w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(SORT_LABEL) as (keyof typeof SORT_LABEL)[]).map((s) => (
              <SelectItem key={s} value={s}>
                {SORT_LABEL[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {failed ? (
        <ErrorPanel
          error={list.error}
          titleAs="h2"
          what="The catalogue"
          onRetry={() => void list.refetch()}
        />
      ) : (
        <DataTable
          label={TABLE_LABEL}
          columns={columns}
          data={items}
          loading={firstLoad}
          empty={empty}
          filterable={false}
          sorting={sorting}
          onSortingChange={onSortingChange}
          getRowId={(p) => p.id}
          onRowActivate={openProduct}
          footer={footer}
          className="max-h-[calc(100dvh-17rem)]"
        />
      )}
    </>
  );
}
